import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import BMIChart from './BMIChart';
import BMIResults from './BMIResults';
import BMIHistory from './BMIHistory';

const { FiActivity, FiTrendingUp, FiHeart, FiTarget } = FiIcons;

const BMICalculator = ({ user }) => {
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [unit, setUnit] = useState('metric'); // metric or imperial
  const [bmi, setBmi] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    // Load user-specific BMI history
    const savedHistory = localStorage.getItem(`bmiHistory_${user.id}`);
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
  }, [user.id]);

  const calculateBMI = () => {
    if (!weight || !height) return;

    let weightKg = parseFloat(weight);
    let heightM = parseFloat(height);

    if (unit === 'imperial') {
      // Convert pounds to kg and inches to meters
      weightKg = weightKg * 0.453592;
      heightM = heightM * 0.0254;
    } else {
      // Convert cm to meters
      heightM = heightM / 100;
    }

    const bmiValue = weightKg / (heightM * heightM);
    const roundedBmi = Math.round(bmiValue * 10) / 10;
    
    setBmi(roundedBmi);

    // Save to user-specific history
    const newEntry = {
      id: Date.now(),
      bmi: roundedBmi,
      weight: parseFloat(weight),
      height: parseFloat(height),
      unit,
      date: new Date().toLocaleDateString(),
      userId: user.id
    };

    const updatedHistory = [newEntry, ...history.slice(0, 9)]; // Keep last 10 entries
    setHistory(updatedHistory);
    localStorage.setItem(`bmiHistory_${user.id}`, JSON.stringify(updatedHistory));
    
    // Also update the global history for backward compatibility
    localStorage.setItem('bmiHistory', JSON.stringify(updatedHistory));
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem(`bmiHistory_${user.id}`);
    localStorage.removeItem('bmiHistory');
  };

  return (
    <div className="py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Welcome Message */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Welcome back, {user.name}!
          </h2>
          <p className="text-gray-600">
            Track your Body Mass Index and monitor your weight loss journey
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            <div className="flex items-center mb-6">
              <SafeIcon icon={FiActivity} className="text-2xl text-blue-500 mr-3" />
              <h2 className="text-2xl font-bold text-gray-800">Calculate Your BMI</h2>
            </div>

            {/* Unit Toggle */}
            <div className="mb-6">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setUnit('metric')}
                  className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                    unit === 'metric'
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-blue-500'
                  }`}
                >
                  Metric (kg/cm)
                </button>
                <button
                  onClick={() => setUnit('imperial')}
                  className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                    unit === 'imperial'
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-blue-500'
                  }`}
                >
                  Imperial (lbs/in)
                </button>
              </div>
            </div>

            {/* Input Fields */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight {unit === 'metric' ? '(kg)' : '(lbs)'}
                </label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder={unit === 'metric' ? 'Enter weight in kg' : 'Enter weight in lbs'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Height {unit === 'metric' ? '(cm)' : '(inches)'}
                </label>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  placeholder={unit === 'metric' ? 'Enter height in cm' : 'Enter height in inches'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={calculateBMI}
                disabled={!weight || !height}
                className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-3 px-6 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
              >
                Calculate BMI
              </motion.button>
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-8"
          >
            {bmi && <BMIResults bmi={bmi} />}
            <BMIChart currentBmi={bmi} />
          </motion.div>
        </div>

        {/* History Section */}
        {history.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12"
          >
            <BMIHistory history={history} onClear={clearHistory} />
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default BMICalculator;